import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  BookingListParams,
  BookingUpdateParams,
  HookParams,
  Order,
  OrderDetail,
  OrderSearchParams,
} from '@/libs/interfaces'
import { bookingsApi } from '@/services/api'

export function useBookingByID({
  params,
  enabled,
  skipErrorRedirect,
}: HookParams<{ orgID: string; storeID: string; orderID: string }> & {
  skipErrorRedirect?: boolean
}) {
  return useQuery<OrderDetail>({
    queryKey: QUERY_KEYS.bookings.id(params.orderID),
    enabled: (enabled ?? true) && !!params.orderID && !!params.orgID && !!params.storeID,
    queryFn: () => bookingsApi.getById(params),
    staleTime: STALE_TIME['1Hour'],
    meta: { skipErrorRedirect },
  })
}

export function useBookingListing({ params, enabled }: HookParams<BookingListParams>) {
  return useQuery<Order[]>({
    queryKey: QUERY_KEYS.bookings.list(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await bookingsApi.list(params)
    },
    placeholderData: (previousData) => previousData,
    staleTime: STALE_TIME['1Hour'], // 1 hour
  })
}

// The backend's `orderStatusSearch` filter only accepts one exact status per request (no
// exclusion/multi-value support) — fetch it once per status and merge, so a caller can show
// "everything except these statuses" without a date filter, using `orderStatusSearch` as the
// server-side mechanism rather than filtering an unfiltered fetch client-side.
export function useBookingListingByStatuses({
  params,
  statuses,
  enabled,
}: {
  params: Omit<BookingListParams, 'orderStatusSearch'>
  statuses: string[]
  enabled?: boolean
}) {
  const queries = useQueries({
    queries: statuses.map((orderStatusSearch) => {
      const statusParams = { ...params, orderStatusSearch }
      return {
        queryKey: QUERY_KEYS.bookings.list(statusParams),
        enabled: enabled ?? true,
        queryFn: async () => await bookingsApi.list(statusParams),
        placeholderData: (previousData?: Order[]) => previousData,
        staleTime: STALE_TIME['1Hour'],
      }
    }),
  })

  return {
    data: queries.flatMap((query) => query.data ?? []),
    isLoading: queries.some((query) => query.isLoading),
  }
}

export function useOrdersSearch({ params, enabled }: HookParams<OrderSearchParams>) {
  return useQuery<Order[]>({
    queryKey: QUERY_KEYS.bookings.search(params),
    enabled: (enabled ?? true) && !!params.orgID,
    queryFn: () => bookingsApi.search(params),
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useBookingCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: bookingsApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.bookings.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useBookingUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: bookingsApi.update,
    onMutate: async (payload: BookingUpdateParams) => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.bookings.all,
      })

      const snapshots = queryClient.getQueriesData<Order[]>({
        queryKey: QUERY_KEYS.bookings.all,
      })

      snapshots.forEach(([queryKey, cachedOrders]) => {
        if (!cachedOrders) return

        queryClient.setQueryData<Order[]>(
          queryKey,
          cachedOrders.map((order) =>
            order.orderID === payload.params.orderID
              ? {
                  ...order,
                  ...(payload.data.orderStatus && {
                    orderStatus: payload.data.orderStatus,
                  }),
                  ...(payload.data.submissionTime && {
                    submissionTime: payload.data.submissionTime,
                  }),
                  ...(payload.data.submittedTime && {
                    submittedTime: payload.data.submittedTime,
                  }),
                  ...(payload.data.pickupTime && {
                    pickupTime: payload.data.pickupTime,
                  }),
                  ...(payload.data.pickedUpTime && {
                    pickedUpTime: payload.data.pickedUpTime,
                  }),
                  services: payload.data.services ?? order.services,
                  products: payload.data.products ?? order.products,
                }
              : order,
          ),
        )
      })

      return { snapshots }
    },
    onError: (_error, _payload, context) => {
      context?.snapshots.forEach(([queryKey, cachedOrders]) => {
        queryClient.setQueryData(queryKey, cachedOrders)
      })
      toast.error(t('common.error'))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.bookings.all,
      })
    },
  })
}

export function useBookingDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID: string; orderID: string }) => bookingsApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.bookings.all,
      })
      toast.success(t('jobs.deleted'))
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useBookingInfinite({ params, enabled }: HookParams<BookingListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.bookings.infinite(params),
    queryFn: ({ pageParam }) =>
      bookingsApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}
